
import { Message } from '@/types/chat-types'
import { ScrollArea } from '@/components/ui/scroll-area'
import { marked } from 'marked'
import DOMPurify from 'isomorphic-dompurify'
import { useEffect, useRef } from 'react'

interface MessageListProps {
  messages: Message[]
  isLoading: boolean
}

export const MessageList = ({ messages, isLoading }: MessageListProps) => {
  const scrollRef = useRef<HTMLDivElement>(null)

  const scrollToBottom = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollIntoView({ behavior: "smooth" })
    }
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages, isLoading])

  const renderMessage = (text: string) => {
    // Process custom title syntax first
    const processedText = text.split('\n').map(line => {
      if (line.startsWith('###') && line.endsWith(':')) {
        const title = line.slice(3, -1).trim()
        return `<h4 class="text-lg font-semibold mb-0.5">${title}:</h4>`
      }
      return line
    }).join('\n')

    // Configure marked options
    marked.setOptions({
      gfm: true,
      breaks: true
    })

    // Parse markdown to HTML and sanitize
    const parsedMarkdown = marked.parse(processedText) as string
    const sanitizedHtml = DOMPurify.sanitize(parsedMarkdown)

    return (
      <div 
        className="markdown-content [&_p]:mb-1 last:[&_p]:mb-0 text-sm"
        dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
      />
    )
  }

  return (
    <ScrollArea className="h-full overflow-y-auto py-6 px-6">
      <div className="flex flex-col gap-4">
        {messages.map((message) => (
          <div
            key={message.id}
            className={`flex ${
              message.sender === 'user' ? 'justify-end' : 'justify-start'
            } relative`}
          >
            <div
              className={`rounded-2xl px-4 py-2 max-w-[85%] shadow-sm ${
                message.sender === 'user'
                  ? 'bg-primary text-primary-foreground ml-12'
                  : 'bg-muted mr-12'
              }`}
            >
              {renderMessage(message.text)}
            </div>
          </div>
        ))}
        {isLoading && (
          <div className="flex justify-start relative">
            <div className="rounded-2xl px-4 py-2 bg-muted mr-12">
              <div className="flex gap-2 items-center h-6">
                <div className="h-2 w-2 animate-bounce rounded-full bg-foreground/50"></div>
                <div className="h-2 w-2 animate-bounce rounded-full bg-foreground/50" style={{ animationDelay: '0.2s' }}></div>
                <div className="h-2 w-2 animate-bounce rounded-full bg-foreground/50" style={{ animationDelay: '0.4s' }}></div>
              </div>
            </div>
          </div>
        )}
        <div ref={scrollRef} />
      </div>
    </ScrollArea>
  )
}
